import React from "react";
import FlagImg from "../agent-tender/FlagImg";
import { urgencyMeta, URGENCY_EXPIRED } from "../../utils/lastMinute";

// TP-Last-Minute pill family — V2 UI rebuild, tokens verbatim from the design
// handoff (Last Minute.dc.html, cards 1a). Every card (agent + club + mobile)
// renders identically from these building blocks.

// Urgency-class pill (card top-right): light tint + strong same-hue text +
// 6px leading dot, 12px/600, padding 4×11 (mobile 4×10, dot 5px — 8a). An
// expired listing recolours to neutral grey.
export const UrgencyPill = ({ urgency, expired = false, mobile = false }) => {
  const m = expired ? URGENCY_EXPIRED : urgencyMeta(urgency);
  const label = expired ? "Expired" : urgencyMeta(urgency).label;
  return (
    <span
      className={`inline-flex items-center rounded-full font-semibold whitespace-nowrap ${
        mobile ? "gap-[5px] px-2.5 py-1 text-[11.5px]" : "gap-1.5 px-[11px] py-1 text-[12px]"
      }`}
      style={{ backgroundColor: m.bg, color: m.text }}
    >
      <span
        className="rounded-full flex-shrink-0"
        style={{ width: mobile ? 5 : 6, height: mobile ? 5 : 6, backgroundColor: m.dot }}
      />
      {label}
    </span>
  );
};

// BUY / LOAN flags — For-sale listings only. 10.5px/700, radius 7, tracking
// .05em, padding 3×8 (mobile 10px/radius 6/2×7 — 8a).
export const BuyLoanChips = ({ buy, loan, mobile = false }) => {
  if (!buy && !loan) return null;
  const chip = `inline-flex items-center border border-[#C9E0FF] bg-[#F4F9FF] font-bold uppercase tracking-[0.05em] text-[#1466D8] ${
    mobile ? "rounded-[6px] px-[7px] py-0.5 text-[10px]" : "rounded-[7px] px-2 py-[3px] text-[10.5px]"
  }`;
  return (
    <>
      {buy && <span className={chip}>Buy</span>}
      {loan && <span className={chip}>Loan</span>}
    </>
  );
};

// "✈︎ Ready to Fly" — the artboard's unicode airplane glyph, verbatim (RULE 2:
// never a library icon).
export const ReadyToFlyPill = ({ mobile = false }) => (
  <span
    className={`inline-flex items-center gap-1.5 rounded-full border border-[#CFE3FB] bg-[#F0F7FF] font-semibold text-[#1466D8] whitespace-nowrap ${
      mobile ? "px-[9px] py-[3px] text-[11.5px]" : "px-2.5 py-1 text-[12px]"
    }`}
  >
    <span aria-hidden="true">✈︎</span>
    Ready to Fly
  </span>
);

// Sibling "Open to short-term" badge.
export const ShortTermPill = ({ mobile = false }) => (
  <span
    className={`inline-flex items-center rounded-full border border-[#CFE3FB] bg-[#F0F7FF] font-semibold text-[#1466D8] whitespace-nowrap ${
      mobile ? "px-[9px] py-[3px] text-[11.5px]" : "px-2.5 py-1 text-[12px]"
    }`}
  >
    Open to short-term
  </span>
);

// Salary band DISPLAY pill — two-tone per artboard: the band strong, " / mo"
// muted. `label` is the formatted "€40–60k / mo" string (utils formatSalaryBand).
export const SalaryBandPill = ({ label, mobile = false }) => {
  if (!label) return null;
  const body = label.replace(/\s*\/\s*mo\s*$/, "");
  const hasMo = body !== label;
  return (
    <span
      className={`inline-flex items-center rounded-full border border-[#E0E6EF] bg-[#F7F9FC] font-semibold text-[#3E5063] whitespace-nowrap ${
        mobile ? "px-[9px] py-[3px] text-[11.5px]" : "px-2.5 py-1 text-[12px]"
      }`}
    >
      {body}
      {hasMo && <span className="font-medium text-[#8A98AC]">&nbsp;/ mo</span>}
    </span>
  );
};

// "Not ready for: [flag] Russia · [flag] Switzerland" public line (Invariant 5).
// Flags via the platform image-flag pattern (FlagImg — brief flags exception),
// artboard sizing 14×10 radius 2. `countries` = [{name, code}].
export const NotReadyForLine = ({ countries }) => {
  if (!countries || countries.length === 0) return null;
  return (
    <p className="flex flex-wrap items-center gap-1.5 text-[12px] text-[#8A98AC]">
      Not ready for:
      {countries.map((c, i) => (
        <span key={c.code || c.name} className="inline-flex items-center gap-1.5">
          {i > 0 && <span aria-hidden="true">·</span>}
          <FlagImg country={c.code || c.name} className="w-[14px] h-[10px] rounded-[2px]" />
          {c.name}
        </span>
      ))}
    </p>
  );
};

const LmPills = {
  UrgencyPill,
  BuyLoanChips,
  ReadyToFlyPill,
  ShortTermPill,
  SalaryBandPill,
  NotReadyForLine,
};

export default LmPills;
